/* eslint-disable no-debugger */
import { FC, useEffect, useMemo } from 'react';
import Container from '@mui/material/Container';
import PageHeader from '@components/pageHeader';
import PortfolioSelection from '@components/portfolioSelection';
import { MovingOutlined } from '@mui/icons-material';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import PortfolioValue from '@components/portfolioValue';
import { useAppDispatch, useAppSelector } from '@src/app/hooks/store';
import { getDashboardAsync } from '@src/app/store/reducers/dashboard/thunks';
import { useAppContext } from '@src/app/hooks/useAppContext';
import { useWindow } from '@src/app/hooks/useWindow';
import CustomSkeleton from '@components/customSkeleton';
import { DashboardChartData, DashboardChart } from './DashboardChart';
import { ScreenState } from '@src/app/enums';
import UnableToLoadData from '@components/unableToLoadData';
import { CommonLoader } from '@components/loaders';
import { PortfolioInfoDetail } from 'clientportalshared';
import PortfolioCard from './PortfolioCard';
import { selectDashboard } from '@src/app/store/reducers/dashboard/selectors';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { DashboardAlert } from './DashboardAlert';

const Home: FC = (): JSX.Element => {
  const { portfoliosSelected, portfoliosIdsSelected, portfoliosCombined, portfolioInfoState } = useAppSelector(selectPortfolios);
  const { data, state } = useAppSelector(selectDashboard);
  const { transitioningMenu } = useAppContext();
  const { resizing } = useWindow();

  const theme = useTheme();
  const dispatch = useAppDispatch();

  const fetchDashboardAsync = async () => {
    await dispatch(
      getDashboardAsync({
        includeValueOverTime: true,
        clientPortfolios: portfoliosSelected,
      })
    );
  };

  const charts: DashboardChartData[] = useMemo(() => {
    const allPortfolioDetails = data?.portfolios?.portfolioInfoDetails ?? [];
    const elements: DashboardChartData[] = allPortfolioDetails
      .filter((detail) => {
        const isSelected = portfoliosIdsSelected.includes(detail?.id ?? -1);
        const isCombined = portfoliosCombined && portfoliosIdsSelected.length > 1 && detail.id === 0 && detail.name === 'Combined';
        return isSelected || isCombined;
      })
      .map((detail: PortfolioInfoDetail) => ({
        valueOverTime: detail.valueOverTime,
        name: detail.name ?? '',
        id: detail.id ?? -1,
      }));
    return elements;
  }, [data, portfoliosSelected, portfoliosCombined]);

  useEffect(() => {
    if (state !== ScreenState.SuccessDataLoaded) {
      fetchDashboardAsync();
    }
  }, []);

  useEffect(() => {
    if (state !== ScreenState.Loading) {
      fetchDashboardAsync();
    }
  }, [portfoliosSelected]);

  if (state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading) {
    return <CommonLoader />;
  }
  if (state === ScreenState.ErrorUnableToLoadData || portfolioInfoState === ScreenState.ErrorUnableToLoadData) {
    return <UnableToLoadData />;
  }
  return (
    <Container>
      <Box display="flex" flexDirection="column" gap={2} mb={6}>
        <DashboardAlert />
        <PageHeader title="Dashboard" icon={MovingOutlined} colour={theme.palette.primary.main}>
          <PortfolioSelection allowCombining={true} />
        </PageHeader>
        <Box
          display="flex"
          alignItems="initial"
          sx={{
            alignItems: { xl: 'center' },
            flexDirection: {
              xl: 'row',
              lg: 'column',
              md: 'column',
              xs: 'column',
              sm: 'column',
            },
          }}
          mt={4}
          mb={4}
          gap={4}
        >
          <PortfolioValue totalValue={data?.portfolios?.totalValue ?? 0} />
        </Box>

        <Typography
          sx={{ color: '#979797', textAlign: { xs: 'start', md: 'center' } }}
          fontWeight={700}
          variant="subtitle2"
          textTransform="uppercase"
        >
          Value Over Time
        </Typography>
        {resizing || transitioningMenu ? <CustomSkeleton variant="rectangular" height={250} numberOfItems={1} /> : <DashboardChart charts={charts} />}

        {data && data?.portfolios?.portfolioInfoDetails && (
          <Box flex={1} display="flex" justifyContent="center">
            <Grid container rowSpacing={{ xs: 2, md: 2 }} columnSpacing={{ md: 2 }}>
              {data.portfolios.portfolioInfoDetails
                .filter((portfolio) => portfolio.id && portfoliosIdsSelected.includes(portfolio.id || -1))
                .map((portfolio, index) => (
                  <PortfolioCard key={index} singleItem={data.portfolios.portfolioInfoDetails?.length === 1} {...portfolio} />
                ))}
            </Grid>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Home;
